import { APP_NAMES } from '@common/core-layer/stores/global.store'
import type { CPNavigationInput } from '@common/navigation-layer/types/navigation'

const LEGACY_CHAT_PATH_PREFIXES = new Set(['load-communities', 'communities/groups'])

const BADGES_NAVIGATION: CPNavigationInput = {
  name: 'account-settings',
  query: {
    activeTab: 'certifications',
    credentialTab: 'badges',
  },
}

export interface PushNotificationNavigationContext {
  refId?: string | null
}

function normalizeRedirectPath(redirectPath: string): string {
  return redirectPath.trim().replace(/^\/+/, '')
}

function isBadgeRedirectPath(redirectPath: string): boolean {
  const normalized = normalizeRedirectPath(redirectPath)
  if (!normalized) return false

  const [pathPart, queryPart = ''] = normalized.split('?')
  const pathKey = pathPart.split('/').filter(Boolean).join('/')
  if (pathKey !== 'account') return false

  const query = new URLSearchParams(queryPart)
  const isCanonical = query.get('credentialTab') === 'badges'
  const isLegacy = query.get('activeTab') === 'Badges'

  return isCanonical || isLegacy
}

function isLegacyBadgeRefId(refId: string | null | undefined): boolean {
  return typeof refId === 'string' && refId.trim().endsWith('-badgeIssued')
}

function conversationIdFromQuery(queryPart: string): string | null {
  const conversationId = new URLSearchParams(queryPart).get('conversationId')?.trim()
  return conversationId || null
}

export function extractConversationIdFromRedirectPath(redirectPath: string): string | null {
  const normalized = normalizeRedirectPath(redirectPath)
  if (!normalized) return null

  const [pathPart, queryPart = ''] = normalized.split('?')
  const pathKey = pathPart.split('/').filter(Boolean).join('/')

  if (LEGACY_CHAT_PATH_PREFIXES.has(pathKey)) {
    return conversationIdFromQuery(queryPart)
  }

  const segments = pathPart.split('/').filter(Boolean)

  if (segments.length === 2 && segments[0] === 'conversations' && segments[1]) {
    return segments[1]
  }

  if (segments.length === 3 && segments[0] === 'communities' && segments[1] === 'conversations' && segments[2]) {
    return segments[2]
  }

  return null
}

export function resolvePushNotificationUrlPath(redirectPath: string, appName: string): string | null {
  const conversationId = extractConversationIdFromRedirectPath(redirectPath)
  if (!conversationId) return null

  if (appName === APP_NAMES.GOKOLLAB) {
    return `/conversations/${conversationId}`
  }

  return `/communities/conversations/${conversationId}`
}

/**
 * Maps legacy FCM `redirectPath` values to Nuxt conversation routes.
 *
 * Backend chat pushes still emit paths from the clientportal-core / GoKollab
 * preview era, e.g. `load-communities?conversationId=<id>` or
 * `communities/groups?conversationId=<id>`.
 */
export function resolvePushNotificationNavigation(
  redirectPath: string,
  context: PushNotificationNavigationContext = {}
): CPNavigationInput | null {
  if (isBadgeRedirectPath(redirectPath) || isLegacyBadgeRefId(context.refId)) {
    return BADGES_NAVIGATION
  }

  const conversationId = extractConversationIdFromRedirectPath(redirectPath)
  if (!conversationId) return null

  return {
    name: 'communities-conversations-conversationId',
    params: { conversationId },
  }
}
